if (!customElements.get('fyre-main-cart')) {
  class FyreMainCart extends HTMLElement {
    constructor() {
      super();

      this.getRelatedProducts = this.getRelatedProducts.bind(this);

      this.observer = new MutationObserver(this.observerCallback.bind(this));
    }

    async connectedCallback() {
      this.selectElements();
      await this.validate();
      this.attachListeners();
      this.getRelatedProducts();
      this.observer.observe(this, { attributes: false, childList: true, subtree: true });
    }

    selectElements() {
      this.container = this.firstElementChild;
      this.editButtons = this.querySelectorAll('#edit');
      this.mainCart = this.querySelector('#main-cart');
      this.removeButtons = this.querySelectorAll('#remove');
      this.frequentlyBoughtContainer = this.querySelector('#frequently-bought-together');
      this.frequentlyBoughtSection = document.querySelector('.frequently-bought-together[data-url]');

      const tag = this.querySelector('& > script#data');
      try {
        this.data = JSON.parse(tag.textContent) ?? {};
      } catch (error) {
        console.log(error);
        this.data = {};
      }
    }

    async validate() {
      const items = this.data?.cart?.items;
      if (!items?.length) return;

      const hidden = [];
      const parentKeys = new Set();
      const parentsWithAddons = [];

      for (const item of items) {
        if (item.properties['_hide_in_cart'] === true || item.properties['_hide_in_cart'] === 'true') {
          hidden.push(item);
        } else if (item.properties['_unique_key']) {
          parentKeys.add(item.properties['_unique_key']);
          if (item.properties['_addon_ids']) parentsWithAddons.push(item);
        }
      }

      // Orphan addon items: hidden items whose parent was removed
      const updates = hidden.reduce((acc, item) => {
        const key = item.properties['_unique_key'];
        if (!key || !parentKeys.has(key)) acc[item.key] = 0;
        return acc;
      }, {});

      // Missing addon items: parents with _addon_ids but some hidden items absent
      const itemsToAdd = [];
      for (const parent of parentsWithAddons) {
        const uniqueKey = parent.properties['_unique_key'];

        const expectedCounts = {};
        for (const addonId of parent.properties['_addon_ids'].split(',')) {
          const variantId = addonId.replace(/^passenger-/, '');
          if (!variantId || isNaN(parseInt(variantId, 10))) continue;
          expectedCounts[variantId] = (expectedCounts[variantId] || 0) + 1;
        }

        const actualCounts = {};
        for (const item of hidden) {
          if (item.properties['_unique_key'] !== uniqueKey) continue;
          const variantId = String(item.variant_id ?? item.id);
          actualCounts[variantId] = (actualCounts[variantId] || 0) + item.quantity;
        }

        for (const [variantId, expectedCount] of Object.entries(expectedCounts)) {
          const missing = expectedCount - (actualCounts[variantId] || 0);
          if (missing > 0) {
            itemsToAdd.push({
              id: parseInt(variantId, 10),
              quantity: missing,
              properties: { _unique_key: uniqueKey, _hide_in_cart: true },
            });
          }
        }
      }

      const hasRemovals = Object.keys(updates).length > 0;
      const hasAdditions = itemsToAdd.length > 0;
      if (!hasRemovals && !hasAdditions) return;

      try {
        let json;
        if (hasRemovals) {
          json = await CartApi.update({ updates, sections: hasAdditions ? undefined : this.data.sectionId });
        }
        if (hasAdditions) {
          json = await CartApi.add({ items: itemsToAdd, sections: this.data.sectionId });
        }
        const html = new DOMParser()
          .parseFromString(json.sections[this.data.sectionId], 'text/html')
          .querySelector('fyre-main-cart');
        this.innerHTML = html.innerHTML;
        this.selectElements();
      } catch (err) {
        console.error(err);
      }
    }

    async getRelatedProducts() {
      const url = this.frequentlyBoughtSection?.dataset.url;
      if (!url) return;

      try {
        const res = await fetch(url);
        const text = await res.text();
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('.frequently-bought-together');

        if (recommendations && recommendations.innerHTML.trim().length && this.frequentlyBoughtContainer) {
          this.frequentlyBoughtContainer.innerHTML = recommendations.outerHTML;
          // Load the Yotpo widget script after the recommendations are loaded
          const script = document.createElement('script');
          script.src = 'https://cdn-widgetsrepository.yotpo.com/v1/loader/7crbRvTq5l8tbtuXWos6vNSBaUucsF4uze0XHL0J';
          script.async = true;
          document.body.appendChild(script);
        }
      } catch (err) {
        console.log(err);
      }
    }

    attachListeners() {
      this._listenersController?.abort();
      this._listenersController = new AbortController();
      const { signal } = this._listenersController;

      this.editButtons?.forEach((button) =>
        button.addEventListener(
          'click',
          async (e) => {
            e.currentTarget.disabled = true;
            const item = this.data.cart.items.find((item) => item.key === e.currentTarget.dataset.key);
            const updates = this.data.cart.items
              .filter((i) => i.properties['_unique_key'] === item.properties['_unique_key'])
              ?.reduce(
                (acc, cur) => {
                  acc[cur.key] = 0;
                  return acc;
                },
                { [item.key]: 0 },
              );

            await CartApi.update({ updates });
            const params = new URLSearchParams(item.properties);
            window.location = `${item.url}&${params.toString()}`;
          },
          { signal },
        ),
      );
      this.removeButtons?.forEach((button) =>
        button.addEventListener(
          'click',
          async (e) => {
            e.currentTarget.disabled = true;
            const itemKey = e.currentTarget.dataset.key;
            const item = this.data.cart.items.find((item) => item.key === itemKey);
            // Only group removals if it's an add-on product
            let updates = {};
            if (item.properties['_addon_ids']) {
              // Remove main product and its add-ons
              updates = this.data.cart.items
                .filter((i) => i.properties['_unique_key'] === item.properties['_unique_key'])
                ?.reduce(
                  (acc, cur) => {
                    acc[cur.key] = 0;
                    return acc;
                  },
                  { [itemKey]: 0 },
                );
            } else {
              // Remove only the clicked item
              updates = { [itemKey]: 0 };
            }

            LoadingManager.toggleFullscreen();
            try {
              const json = await CartApi.update({ updates, sections: this.data.sectionId });
              const html = new DOMParser()
                .parseFromString(json.sections[this.data.sectionId], 'text/html')
                .querySelector('fyre-main-cart');
              this.innerHTML = html.innerHTML;
              if (json.items.length) await this.getRelatedProducts();
            } catch (err) {
              console.log(err);
            }
            LoadingManager.toggleFullscreen();
          },
          { signal },
        ),
      );
    }

    observerCallback(mutationList, observer) {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          if (this.frequentlyBoughtContainer?.contains(mutation.target)) return;
          // console.log('A child node has been added or removed within:');
          // console.log(mutation.target);
          this.selectElements();
          this.attachListeners();
        }
      }
    }

    disconnectedCallback() {
      this.observer.disconnect();
    }
  }
  customElements.define('fyre-main-cart', FyreMainCart);
}
